import { useEffect, useState } from "react";
import { Box, Button, Checkbox, FormControlLabel, FormGroup, Stack, TextField, Typography } from "@mui/material";

import { useDialogs } from "@toolpad/core/useDialogs";
import { useDispatch, useSelector } from "react-redux";

import { RootState } from "@store/store";
import { Person } from "@interfaces/persons";
import { Notificacion } from "./Notificacion";
import { filterByNestedCodes } from "@helpers/generals";
import { NewMajorityShareholder } from "@interfaces/slices";
import { setDialogConfig } from "@store/slices/componentsSlice";
import { setApplicationStateCode } from "@store/slices/componentsSlice";
import { useLazyGetNewMajorityShareholderQuery, useLazyGetPersonsQuery } from "@services/api/persons";

interface NuevoAccionistaMayoritarioProps extends NewMajorityShareholder {
    onConfirmClick: () => void;
}

export const NuevoAccionistaMayoritario = (props: NuevoAccionistaMayoritarioProps) => {
    const dialogs = useDialogs();
    const dispatch = useDispatch();

    const [saving, setSaving] = useState<boolean>(false);
    const [comment, setComment] = useState<string>();
    const [persons, setPersons] = useState<Array<Person>>([]);
    const [personSelected, setPersonSelected] = useState<Person>();
    const [majorityShareholderOld, setMajorityShareholderOld] = useState<Person>();

    const userInfo = useSelector((state: RootState) => state.session.user);
    const applicationSelected = useSelector((state: RootState) => state.components.applicationSelected);

    const [triggerGetPersons] = useLazyGetPersonsQuery();
    const [triggerNeMajorityShareholder] = useLazyGetNewMajorityShareholderQuery();

    const handleOnBackClicked = () => {
        dispatch(setDialogConfig());
    };

    const handleOnContinueClicked = async () => {
        try {
            setSaving(true);

            if (personSelected === undefined || personSelected === null) {
                setSaving(false);
                return;
            }

            if (comment?.trim().length === 0) {
                setSaving(false);
                return;
            }

            await triggerNeMajorityShareholder(personSelected.id ?? "0-0-0-0-0").unwrap();

            dispatch(setApplicationStateCode("CSDAL"));

            setSaving(false);
            dispatch(setDialogConfig());
            props.onConfirmClick?.();
        } catch (error) {
            console.log("Error al actualizar el accionista mayoritario:", error);
            setSaving(false);
            await dialogs.open(Notificacion, {
                type: "WARNING",
                text: "No se pudo actualizar la información.",
            });
        }
    };

    const handleOnPersonChanged = (person: Person) => {
        setPersonSelected(person);
    };

    const reloadInfo = async () => {
        try {
            let params = new URLSearchParams([["q", `loan_application_id:${applicationSelected.id}`]]);
            const personsData = await triggerGetPersons(params.toString()).unwrap();

            const othersPerson = filterByNestedCodes(personsData, (person) => person.person_types_detail, [
                "PTAM",
                "PTPR",
                "PTACAV",
                "PTRL",
            ]).filter((person) => person.is_shareholder && person.is_aval_onboarding === null);

            const old = filterByNestedCodes(personsData, (person) => person.person_types_detail, ["PTAM"]).find(
                (person) => person.is_aval_onboarding === false,
            );

            if (old) {
                setMajorityShareholderOld(old);
            }

            setPersons(othersPerson);
        } catch (error) {
            console.log("Error al cargar la información de cuadro accionario.");
        }
    };

    useEffect(() => {
        if (applicationSelected.id === "0-0-0-0-0" || applicationSelected.id === undefined) return;

        reloadInfo();
    }, [applicationSelected.id]);

    return (
        <Box margin={4} maxWidth={"300px"}>
            <Stack textAlign={"center"} rowGap={3}>
                <Typography variant="body1" fontWeight={600} color="main3">
                    Está seguro que el accionista mayoritario proporcionado no es el correcto
                </Typography>

                <Typography variant="body1" fontWeight={500} color="main2">
                    {[
                        majorityShareholderOld?.name,
                        majorityShareholderOld?.last_name,
                        majorityShareholderOld?.last_name_2,
                    ].join(" ")}
                </Typography>
                <Typography variant="body1" fontWeight={600} color="main3">
                    Seleccione el accionista mayoritario correcto
                </Typography>
            </Stack>
            <Stack rowGap={3} mt={2}>
                <FormGroup>
                    {persons.map((person) => (
                        <FormControlLabel
                            id={person.id}
                            control={
                                <Checkbox
                                    onChange={() => {
                                        handleOnPersonChanged(person);
                                    }}
                                    checked={person.id === personSelected?.id}
                                />
                            }
                            disabled={saving}
                            label={[person.name, person.last_name, person.last_name_2].join(" ")}
                        />
                    ))}
                </FormGroup>
                <TextField
                    multiline
                    required
                    rows={2}
                    label={"Agregar Comentario"}
                    slotProps={{
                        htmlInput: {
                            maxLength: 500,
                        },
                    }}
                    onChange={(event) => setComment(event.target.value)}
                    value={comment}
                />
            </Stack>
            <Stack direction={"row"} justifyContent={"space-around"} mt={4}>
                <Button variant="blue_outlined" size={"large"} onClick={handleOnBackClicked} disabled={saving}>
                    Regresar
                </Button>
                <Button
                    variant="contained"
                    color="main2"
                    size={"large"}
                    onClick={handleOnContinueClicked}
                    disabled={personSelected === undefined || comment === undefined || comment.trim().length === 0}
                    loading={saving}
                >
                    Continuar
                </Button>
            </Stack>
        </Box>
    );
};
